import { useEffect, useState } from "react";
import { Header } from "./Header";
import { Stepper } from "./Stepper";
import { SuccessSummary } from "./SuccessSummary";
import { FormBody } from "./FormBody";
import { Footer } from "./Footer";
import { useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import imageCompression from "browser-image-compression";
import { BASE_URL } from "../../../../config/api";

/* ---------------- VALIDATION HELPERS ---------------- */
const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const isValidImei = (imei) =>
  /^\d{15}$/.test(imei);

const isValidSerial = (serial) =>
  /^[A-Za-z0-9-]{5,}$/.test(serial);

const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

/* ---------------- STEPS ---------------- */
const steps = [
  { id: 1, name: "Select Plan" },
  { id: 2, name: "Contact Details" },
  { id: 3, name: "Device Details" },
  { id: 4, name: "Payment" },
];

export default function CreateCustomer() {
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [wCustomerId, setWCustomerId] = useState(null);
  const location = useLocation();
  const existingCustomer = location.state?.customer || null;
  const verifiedMobile = location.state?.verifiedMobile || "";
  const [confirmed, setConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [noTemplateMessage, setNoTemplateMessage] = useState("");
  const [isPayLater, setIsPayLater] = useState(0); // 0 = Pay Now, 1 = Pay Later
  const [uploading, setUploading] = useState({
  billPhoto: false,
  devicePhoto: false,
  customerPhoto: false,
});

  const [walletBalance, setWalletBalance] = useState(null);
  const [walletLoading, setWalletLoading] = useState(false);

  const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");
  const companyId = authUser?.company_id;

  /* ---------------- FORM STATE ---------------- */
  const [form, setForm] = useState({
    name: "",
    mobile: "",
    email: "",
    state: "",
    city: "",
    pincode: "",
    location: "",
    addressLine1: "",
    addressLine2: "",
    category: "",
    categoryName: "",
    deviceBrand: "",
    brandName: "",
    deviceModel: "",
    mopPrice: "",
    expiryDate: "",
    paymentMode: "",
    deviceImei: "",
    deviceImei2: "",
    serialNumber: "",
    billPhoto: "",       // URL
    devicePhoto: "",     // URL
    customerPhoto: "", 
    modelId: null,
  });

  /* ---------------- STATE ---------------- */
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [priceTemplates, setPriceTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [modelResults, setModelResults] = useState([]);
  const [modelLoading, setModelLoading] = useState(false);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pinLoading, setPinLoading] = useState(false);
  const [certificate, setCertificate] = useState(null);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState(null);
  console.log("payment status: ", paymentConfig);
  const [loadingPaymentConfig, setLoadingPaymentConfig] = useState(false);
  const [retailer, setRetailer] = useState(null);
  const [retailerLoading, setRetailerLoading] = useState(false);
  const [calculatedPayouts, setCalculatedPayouts] = useState({
    retailerPayout: 0,
    employeePayout: 0,
    retailerPending: 0,
    otherpayout:0,
    companypayout: 0,
    productPrice: 0,
  });


  /* ---------------- HELPERS ---------------- */
  const updateField = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    setErrors((e) => ({ ...e, [field]: "" }));
  };

  const handleFileChange = (field, file) => {
  if (!file) return;

  if (!file.type.startsWith("image/")) {
    toast.error("Only image files allowed");
    return;
  }

  setForm((prev) => ({
    ...prev,
    [field]: file,
  }));

  setErrors((prev) => ({ ...prev, [field]: "" }));
};

  /* ---------------- DEVICE MODEL SEARCH ---------------- */
  const searchDeviceModels = async (query) => {
    if (!query || query.length < 2) return setModelResults([]);
    try {
      setModelLoading(true);
      const res = await fetch(
        `${BASE_URL}warranty/device-models?search=${query}`
      );
      const data = await res.json();
      setModelResults(data?.data?.data || []);
    } catch {
      toast.error("Failed to load models");
    } finally {
      setModelLoading(false);
    }
  };

  /* ---------------- PINCODE ---------------- */
  const fetchPincodeDetails = async (pin) => {
    if (pin.length !== 6) return;
    try {
      setPinLoading(true);
      const res = await fetch(`${BASE_URL}pincode/${pin}`);
      const data = await res.json();
      if (data.success) {
        updateField("state", data.state);
        updateField("city", data.district);
      } else {
        toast.error("Invalid pincode");
      }
    } finally {
      setPinLoading(false);
    }
  };

  /* ---------------- FETCH DATA ---------------- */
  useEffect(() => {
    fetch(`${BASE_URL}warranty/get-categories`)
      .then((r) => r.json())
      .then(setCategories);
    fetch(`${BASE_URL}warranty/get-brands`)
      .then((r) => r.json())
      .then((d) => setBrands(d.data || []));
  }, []);

  /* ---------------- PRICE TEMPLATES ---------------- */
  const handleViewTemplates = async () => {
    if (!form.category || !form.mopPrice) {
      toast.error("Missing data");
      return;
    }

    try {
      setPricingLoading(true);
      setNoTemplateMessage("");      // reset
      setPriceTemplates([]);         // clear old data

      const res = await fetch(`${BASE_URL}warranty/matching-price-templates`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product_price: Number(form.mopPrice),
          category_id: Number(form.category),
          company_id: Number(companyId),
        }),
      });

      const data = await res.json();

      // ❌ NO DATA FOUND
      if (data.status === false || !data.data?.length) {
        setNoTemplateMessage(
          data.message || "No matching price templates found"
        );
        return;
      }

      // ✅ DATA FOUND
      setPriceTemplates(data.data);
    } catch (err) {
      toast.error("Failed to fetch templates");
    } finally {
      setPricingLoading(false);
    }
  };


  const fetchWalletBalance = async () => {
  try {
    setWalletLoading(true);

    // ✅ detect role
    const isPromoter = authUser?.role === 6;

    // ✅ decide correct user_id
    const userId = isPromoter
      ? authUser?.retailer_id || authUser?.parent_id || authUser?.created_by_id
      : authUser?.id;

    const res = await fetch(
      `${BASE_URL}company/sync-wallet-from-zoho?company_id=${authUser.company_id}&user_id=${userId}`
    );

    const data = await res.json();

    if (data?.status) {
      setWalletBalance(Number(data.wallet_balance));
    }
  } catch (err) {
    console.error("Wallet fetch failed:", err);
  } finally {
    setWalletLoading(false);
  }
};

useEffect(() => {
  if (currentStep === 4) {
    fetchWalletBalance();
  }
}, [currentStep]);
  /* ---------------- VALIDATION ---------------- */
  const validateStep = () => {
  const e = {};

  if (currentStep === 1) {
    if (!selectedTemplate) e.template = "Please select a warranty plan";
  }

  if (currentStep === 2) {
    if (!form.name) e.name = "Name is required";
    if (!form.email) e.email = "Email is required";
    if (!form.pincode) e.pincode = "Pincode is required";
  }

  if (currentStep === 3) {
    if (!form.deviceImei) e.deviceImei = "IMEI is required";
    if (!form.serialNumber) e.serialNumber = "Serial number is required";
    if (!form.billPhoto) e.billPhoto = "Bill photo required";
  }

  setErrors(e);
  return e;   // 🔥 return error object instead of boolean
};


  /* ---------------- CREATE CUSTOMER ---------------- */
  // const createCustomer = async () => {
  //   try {
  //     const res = await fetch(
  //       `${BASE_URL}warranty/create-customer-new`,
  //       {
  //         method: "POST",
  //         headers: { "Content-Type": "application/json" },
  //         body: JSON.stringify({
  //           name: form.name,
  //           mobile: form.mobile,
  //           email: form.email,
  //           state: form.state,
  //           city: form.city,
  //           pincode: form.pincode,
  //           location: form.location,
  //           retailer_id: authUser?.id,
  //           created_by: authUser?.id,
  //           agent_id:authUser.senior_id,
  //           company_id:authUser.company_id,
  //           address1:form.addressLine1,
  //           address2:form.addressLine2,
  //         }),
  //       }
  //     );

  //     const data = await res.json();

  //     if (!res.ok) {
  //       throw new Error(data?.message || "Server error");
  //     }

  //     // ✅ EXISTING CUSTOMER
  //     if (data.message === "Customer already exists") {
  //       setWCustomerId(data.data.id);
  //       toast.success("Customer already exists. Using existing customer.");
  //       return true;
  //     }

  //     // ✅ NEW CUSTOMER CREATED
  //     if (data.message && data.data?.id) {
  //       setWCustomerId(data.data.id);
  //       toast.success("Customer created successfully");
  //       return true;
  //     }

  //     throw new Error("Failed to create customer");
  //   } catch (err) {
  //     toast.error(err.message || "Failed to create customer");
  //     return false;
  //   }
  // };

  const createCustomer = async () => {
    try {
      const localRole = Number(authUser?.role);

      const payload = {
        name: form.name,
        mobile: form.mobile,
        email: form.email,
        state: form.state,
        city: form.city,
        pincode: form.pincode,
        location: form.location,

        // 🔥 CONDITIONAL LOGIC
        retailer_id:
          localRole === 6
            ? authUser?.senior_id
            : authUser?.id,

        created_by: authUser?.id,

        agent_id: localRole === 6
            ? authUser?.senior_id
            : authUser?.agent_id,
        company_id: authUser?.company_id,
        address1: form.addressLine1,
        address2: form.addressLine2,
      };

      const res = await fetch(
        `${BASE_URL}warranty/create-customer-new`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.message || "Server error");
      }

      if (data.message === "Customer already exists") {
        setWCustomerId(data.data.id);
        toast.success("Customer already exists. Using existing customer.");
        return true;
      }

      if (data.message && data.data?.id) {
        setWCustomerId(data.data.id);
        toast.success("Customer created successfully");
        return true;
      }

      throw new Error("Failed to create customer");
    } catch (err) {
      toast.error(err.message || "Failed to create customer");
      return false;
    }
  };

  const uploadImage = async (file, tag) => {
  try {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("tag", tag);

    const res = await fetch(`${BASE_URL}warranty/upload-file`, {
      method: "POST",
      body: fd,
    });

    const data = await res.json();

    if (!res.ok || !data.status) {
      throw new Error(data.message || "Upload failed");
    }

    return data.url;
  } catch (err) {
    toast.error(err.message || "Image upload failed");
    return null;
  }
};

const handleImageUpload = async (field, file, tag) => {
  if (!file) return;

  try {
    const options = {
      maxSizeMB: 5,              // target max size
      maxWidthOrHeight: 1920,   // resize large images
      useWebWorker: true,
    };

    const compressedFile = await imageCompression(file, options);

    console.log(
      "Original size:",
      (file.size / 1024 / 1024).toFixed(2),
      "MB"
    );

    console.log(
      "Compressed size:",
      (compressedFile.size / 1024 / 1024).toFixed(2),
      "MB"
    );

    if (compressedFile.size > 5 * 1024 * 1024) {
      toast.error("Image still larger than 5MB after compression");
      return;
    }

    setUploading((p) => ({ ...p, [field]: true }));

    const toastId = toast.loading("Uploading image, please wait...");

    const fd = new FormData();
    fd.append("file", compressedFile);
    fd.append("tag", tag);

    const res = await fetch(`${BASE_URL}warranty/upload-file`, {
      method: "POST",
      body: fd,
    });

    const data = await res.json();

    const fileUrl = data?.data?.file_url;
    if (!fileUrl) throw new Error("Upload failed");

    setForm((prev) => ({
      ...prev,
      [field]: fileUrl,
    }));

    setErrors((prev) => ({ ...prev, [field]: "" }));

    toast.success("Image uploaded successfully", { id: toastId });

  } catch (err) {
    toast.error(err.message || "Upload failed");
  } finally {
    setUploading((p) => ({ ...p, [field]: false }));
  }
};


const createWarranty = async ({ is_pay_later }) => {
  if (isSubmitting) return null;

  try {
    setIsSubmitting(true);

     if (!wCustomerId) {
      toast.error("Customer not found");
      return null;
    }

    if (!selectedTemplate?.warranty_product) {
      toast.error("Warranty product missing");
      return null;
    }
    const product = selectedTemplate.warranty_product;

    const payload = {
      imei1: form.deviceImei,
      imei2: form.deviceImei2 || null,
      serial: form.serialNumber,

      brand_id: Number(form.deviceBrand),
      brand_name: form.brandName,

      category_id: Number(form.category),
      category_name: form.categoryName,

      product_id: product.id,
      product_name: product.name,

      available_claim: product.claims,
      expiry_date: form.expiryDate,

      document_url: form.billPhoto,
      link1: form.devicePhoto,
      link2: form.customerPhoto,

      w_customer_id: wCustomerId,
      device_price: Number(form.mopPrice),
      retailer_id: authUser?.id,
      name: form.name,
      model: form.deviceModel,
      model_id: form.modelId,
      product_mrp:product.mrp,
      product_price: calculatedPayouts.retailerPending,
      retailer_payout: calculatedPayouts.retailerPayout,
      employee_payout: calculatedPayouts.employeePayout,
      other_payout: calculatedPayouts.otherpayout,
      company_payout: calculatedPayouts.companypayout,
      agent_id:authUser?.senior_id,
      company_id:authUser.company_id,
      created_by: authUser?.id,
      is_pay_later,
    };

    const res = await fetch(`${BASE_URL}warranty/create-warranty`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const data = await res.json();
    if (!res.ok || !data.device?.id) {
        throw new Error(data?.message || "Warranty creation failed");
      }

    return data.device.id;
  } catch (err) {
    toast.error(err.message || "Warranty creation failed");
    return null;
  }
};

  const generateWarrantyCertificate = async () => {
  try {
    if (!form.deviceImei) {
      toast.error("IMEI is missing");
      return false;
    }

    const res = await fetch(
      `${BASE_URL}warranty/generate-wcertificate`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          imei1: form.deviceImei,   // ✅ ONLY PAYLOAD
        }),
      }
    );

    const data = await res.json();

    if (!res.ok || data.success === false) {
      throw new Error(data?.message || "Certificate generation failed");
    }

    // ✅ STORE CERTIFICATE INFO
    setCertificate({
      certificateId: data.certificate_id,
      certificateUrl: data.certificate_url,
    });

    toast.success("Warranty certificate generated");
    return true;
  } catch (err) {
    toast.error(err.message || "Failed to generate certificate");
    return false;
  }
};

  useEffect(() => {
    const fetchRetailer = async () => {
      try {
        setRetailerLoading(true);

        if (!authUser?.id) {
          console.warn("authUser.id missing, get-users API skipped");
          return;
        }

        const res = await fetch(
          `${BASE_URL}common/get-users?id=${authUser.id}`
        );

        const json = await res.json();

        if (!res.ok || !json.status || !json.data?.length) {
          throw new Error("Failed to fetch retailer data");
        }

        const user = json.data[0];

        setRetailer({
          id: user.id,
          name: user.business_name || user.name,
          mobile: user.contact_phone,
          email: user.contact_email,
          company_id: user.company_id,
          agent_id: user.senior_id,
          role: user.role,

          pay_now: Number(user.pay_now),
          pay_later: Number(user.pay_later),
          admin_paynow_status: Number(user.admin_paynow_status),
          admin_paylater_status: Number(user.admin_paylater_status),
        });
      } catch (err) {
        console.error("get-users failed:", err);
        toast.error(err.message || "Unable to load retailer details");
      } finally {
        setRetailerLoading(false);
      }
    };

    fetchRetailer();
  }, [authUser?.id]); // ✅ IMPORTANT

  useEffect(() => {
  if (!retailer) return;

  setPaymentConfig({
    pay_now: retailer.pay_now,
    pay_later: retailer.pay_later,
    admin_paynow_status: retailer.admin_paynow_status,
    admin_paylater_status: retailer.admin_paylater_status,
  });
}, [retailer]);

  /* ---------------- NAVIGATION ---------------- */
  const handleNext = async () => {

  const isValid = validateStep();

  const validationErrors = validateStep();

  if (Object.keys(validationErrors).length > 0) {
    const firstError = Object.values(validationErrors)[0];
    toast.error(firstError);
    return;
  }

  if (!isValid) {
    // 🔥 GET FIRST ERROR MESSAGE
    const firstError = Object.values(errors)[0];

    if (firstError) {
      toast.error(firstError);
    } else {
      toast.error("Please fill all required fields");
    }

    return;
  }

  // STEP 2 → CREATE CUSTOMER
  if (currentStep === 2) {
    const ok = await createCustomer();
    if (!ok) return;
  }

  // STEP 4 → CREATE WARRANTY
  if (currentStep === 4) {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);

      if (isPayLater === 1) {
        const warrantyId = await createWarranty({
          is_pay_later: 1,
        });

        if (!warrantyId) return;
      }

      const certOk = await generateWarrantyCertificate();
      if (!certOk) return;

      setSubmitted(true);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }

    return;
  }

  setCurrentStep((s) => s + 1);
};


  const handleBack = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  useEffect(() => {
  if (currentStep !== 4) {
    setConfirmed(false);
  }
}, [currentStep]);


const isRetailerMobile =
  typeof window !== "undefined" &&
  window.innerWidth < 640 &&
  authUser?.role === 5;

  /* ---------------- UI ---------------- */
  return (
    <div
      className={`
        rounded-2xl p-3 transition-all duration-300
        ${
          isRetailerMobile
            ? "bg-white/10 backdrop-blur-2xl border border-white/30 shadow-[0_20px_50px_rgba(31,38,135,0.25)] mt-5"
            : "bg-white border shadow"
        }
      `}
    >
      <Header />
      <Stepper steps={steps} currentStep={currentStep} />

      {submitted ? (
        <SuccessSummary
            form={form}
            certificate={certificate}
          />
      ) : (
        <>
          <FormBody
            currentStep={currentStep}
            form={form}
            errors={errors}
            updateField={updateField}
            categories={categories}
            brands={brands}
            modelResults={modelResults}
            modelLoading={modelLoading}
            onSearchModel={searchDeviceModels}
            onViewTemplates={handleViewTemplates}
            priceTemplates={priceTemplates}
            selectedTemplate={selectedTemplate}
            setSelectedTemplate={setSelectedTemplate}
            noTemplateMessage={noTemplateMessage}
            loading={pricingLoading}
            onPincodeChange={fetchPincodeDetails}
            pinLoading={pinLoading}
            handleImageUpload={handleImageUpload}
            uploading={uploading}
            confirmed={confirmed}
            setConfirmed={setConfirmed}
            isPhoneVerified={isPhoneVerified}
            setIsPhoneVerified={setIsPhoneVerified}
            setCalculatedPayouts={setCalculatedPayouts}
            paymentConfig={paymentConfig}
            loadingPaymentConfig={loadingPaymentConfig}
            retailer={retailer}
            retailerLoading={retailerLoading}   
            setIsPayLater={setIsPayLater}
            wCustomerId={wCustomerId}
            walletBalance={walletBalance}
            walletLoading={walletLoading}
            onNext={handleNext}
          />

          <Footer
            currentStep={currentStep}
            onBack={handleBack}
            onNext={handleNext}
            isLast={currentStep === steps.length}
            confirmed={confirmed}
            isSubmitting={isSubmitting}
          />
        </>
      )}
    </div>
  );
}

// *************************original working code********************

// import { useEffect, useState } from "react";
// import { Header } from "./Header";
// import { Stepper } from "./Stepper";
// import { SuccessSummary } from "./SuccessSummary";
// import { FormBody } from "./FormBody";
// import { Footer } from "./Footer";
// import { useLocation } from "react-router-dom";
// import toast from "react-hot-toast";
// import imageCompression from "browser-image-compression";
// import { BASE_URL } from "../../../../config/api";

// /* ---------------- VALIDATION HELPERS ---------------- */
// const isValidEmail = (email) =>
//   /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// const isValidImei = (imei) =>
//   /^\d{15}$/.test(imei);

// const isValidSerial = (serial) =>
//   /^[A-Za-z0-9-]{5,}$/.test(serial);

// const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

// /* ---------------- STEPS ---------------- */
// const steps = [
//   { id: 1, name: "Select Plan" },
//   { id: 2, name: "Contact Details" },
//   { id: 3, name: "Device Details" },
//   { id: 4, name: "Payment" },
// ];

// export default function CreateCustomer() {
//   const [currentStep, setCurrentStep] = useState(1);
//   const [errors, setErrors] = useState({});
//   const [submitted, setSubmitted] = useState(false);
//   const [wCustomerId, setWCustomerId] = useState(null);
//   const location = useLocation();
//   const existingCustomer = location.state?.customer || null;
//   const verifiedMobile = location.state?.verifiedMobile || "";
//   const [confirmed, setConfirmed] = useState(false);
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [noTemplateMessage, setNoTemplateMessage] = useState("");
//   const [isPayLater, setIsPayLater] = useState(0); // 0 = Pay Now, 1 = Pay Later
//   const [uploading, setUploading] = useState({
//   billPhoto: false,
//   devicePhoto: false,
//   customerPhoto: false,
// });

//   const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");
//   const companyId = authUser?.company_id;

//   /* ---------------- FORM STATE ---------------- */
//   const [form, setForm] = useState({
//     name: "",
//     mobile: "",
//     email: "",
//     state: "",
//     city: "",
//     pincode: "",
//     location: "",
//     addressLine1: "",
//     addressLine2: "",
//     category: "",
//     categoryName: "",
//     deviceBrand: "",
//     brandName: "",
//     deviceModel: "",
//     mopPrice: "",
//     expiryDate: "",
//     paymentMode: "",
//     deviceImei: "",
//     deviceImei2: "",
//     serialNumber: "",
//     billPhoto: "",       // URL
//     devicePhoto: "",     // URL
//     customerPhoto: "", 
//     modelId: null,
//   });

//   /* ---------------- STATE ---------------- */
//   const [categories, setCategories] = useState([]);
//   const [brands, setBrands] = useState([]);
//   const [priceTemplates, setPriceTemplates] = useState([]);
//   const [selectedTemplate, setSelectedTemplate] = useState(null);
//   const [modelResults, setModelResults] = useState([]);
//   const [modelLoading, setModelLoading] = useState(false);
//   const [pricingLoading, setPricingLoading] = useState(false);
//   const [pinLoading, setPinLoading] = useState(false);
//   const [certificate, setCertificate] = useState(null);
//   const [isPhoneVerified, setIsPhoneVerified] = useState(false);
//   const [paymentConfig, setPaymentConfig] = useState(null);
//   console.log("payment status: ", paymentConfig);
//   const [loadingPaymentConfig, setLoadingPaymentConfig] = useState(false);
//   const [retailer, setRetailer] = useState(null);
//   const [retailerLoading, setRetailerLoading] = useState(false);
//   const [calculatedPayouts, setCalculatedPayouts] = useState({
//     retailerPayout: 0,
//     employeePayout: 0,
//     retailerPending: 0,
//     otherpayout:0,
//     companypayout: 0,
//     productPrice: 0,
//   });


//   /* ---------------- HELPERS ---------------- */
//   const updateField = (field, value) => {
//     setForm((p) => ({ ...p, [field]: value }));
//     setErrors((e) => ({ ...e, [field]: "" }));
//   };

//   const handleFileChange = (field, file) => {
//   if (!file) return;

//   if (!file.type.startsWith("image/")) {
//     toast.error("Only image files allowed");
//     return;
//   }

//   setForm((prev) => ({
//     ...prev,
//     [field]: file,
//   }));

//   setErrors((prev) => ({ ...prev, [field]: "" }));
// };

//   /* ---------------- DEVICE MODEL SEARCH ---------------- */
//   const searchDeviceModels = async (query) => {
//     if (!query || query.length < 2) return setModelResults([]);
//     try {
//       setModelLoading(true);
//       const res = await fetch(
//         `${BASE_URL}warranty/device-models?search=${query}`
//       );
//       const data = await res.json();
//       setModelResults(data?.data?.data || []);
//     } catch {
//       toast.error("Failed to load models");
//     } finally {
//       setModelLoading(false);
//     }
//   };

//   /* ---------------- PINCODE ---------------- */
//   const fetchPincodeDetails = async (pin) => {
//     if (pin.length !== 6) return;
//     try {
//       setPinLoading(true);
//       const res = await fetch(`${BASE_URL}pincode/${pin}`);
//       const data = await res.json();
//       if (data.success) {
//         updateField("state", data.state);
//         updateField("city", data.district);
//       } else {
//         toast.error("Invalid pincode");
//       }
//     } finally {
//       setPinLoading(false);
//     }
//   };

//   /* ---------------- FETCH DATA ---------------- */
//   useEffect(() => {
//     fetch(`${BASE_URL}warranty/get-categories`)
//       .then((r) => r.json())
//       .then(setCategories);
//     fetch(`${BASE_URL}warranty/get-brands`)
//       .then((r) => r.json())
//       .then((d) => setBrands(d.data || []));
//   }, []);

//   /* ---------------- PRICE TEMPLATES ---------------- */
//   const handleViewTemplates = async () => {
//     if (!form.category || !form.mopPrice) {
//       toast.error("Missing data");
//       return;
//     }

//     try {
//       setPricingLoading(true);
//       setNoTemplateMessage("");      // reset
//       setPriceTemplates([]);         // clear old data

//       const res = await fetch(`${BASE_URL}warranty/matching-price-templates`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({
//           product_price: Number(form.mopPrice),
//           category_id: Number(form.category),
//           company_id: Number(companyId),
//         }),
//       });

//       const data = await res.json();

//       // ❌ NO DATA FOUND
//       if (data.status === false || !data.data?.length) {
//         setNoTemplateMessage(
//           data.message || "No matching price templates found"
//         );
//         return;
//       }

//       // ✅ DATA FOUND
//       setPriceTemplates(data.data);
//     } catch (err) {
//       toast.error("Failed to fetch templates");
//     } finally {
//       setPricingLoading(false);
//     }
//   };

//   /* ---------------- VALIDATION ---------------- */
//   const validateStep = () => {
//   const e = {};

//   if (currentStep === 1) {
//     if (!selectedTemplate) e.template = "Please select a warranty plan";
//   }

//   if (currentStep === 2) {
//     if (!form.name) e.name = "Name is required";
//     if (!form.email) e.email = "Email is required";
//     if (!form.pincode) e.pincode = "Pincode is required";
//   }

//   if (currentStep === 3) {
//     if (!form.deviceImei) e.deviceImei = "IMEI is required";
//     if (!form.serialNumber) e.serialNumber = "Serial number is required";
//     if (!form.billPhoto) e.billPhoto = "Bill photo required";
//   }

//   setErrors(e);
//   return e;   // 🔥 return error object instead of boolean
// };


//   /* ---------------- CREATE CUSTOMER ---------------- */
//   // const createCustomer = async () => {
//   //   try {
//   //     const res = await fetch(
//   //       `${BASE_URL}warranty/create-customer-new`,
//   //       {
//   //         method: "POST",
//   //         headers: { "Content-Type": "application/json" },
//   //         body: JSON.stringify({
//   //           name: form.name,
//   //           mobile: form.mobile,
//   //           email: form.email,
//   //           state: form.state,
//   //           city: form.city,
//   //           pincode: form.pincode,
//   //           location: form.location,
//   //           retailer_id: authUser?.id,
//   //           created_by: authUser?.id,
//   //           agent_id:authUser.senior_id,
//   //           company_id:authUser.company_id,
//   //           address1:form.addressLine1,
//   //           address2:form.addressLine2,
//   //         }),
//   //       }
//   //     );

//   //     const data = await res.json();

//   //     if (!res.ok) {
//   //       throw new Error(data?.message || "Server error");
//   //     }

//   //     // ✅ EXISTING CUSTOMER
//   //     if (data.message === "Customer already exists") {
//   //       setWCustomerId(data.data.id);
//   //       toast.success("Customer already exists. Using existing customer.");
//   //       return true;
//   //     }

//   //     // ✅ NEW CUSTOMER CREATED
//   //     if (data.message && data.data?.id) {
//   //       setWCustomerId(data.data.id);
//   //       toast.success("Customer created successfully");
//   //       return true;
//   //     }

//   //     throw new Error("Failed to create customer");
//   //   } catch (err) {
//   //     toast.error(err.message || "Failed to create customer");
//   //     return false;
//   //   }
//   // };

//   const createCustomer = async () => {
//     try {
//       const localRole = Number(authUser?.role);

//       const payload = {
//         name: form.name,
//         mobile: form.mobile,
//         email: form.email,
//         state: form.state,
//         city: form.city,
//         pincode: form.pincode,
//         location: form.location,

//         // 🔥 CONDITIONAL LOGIC
//         retailer_id:
//           localRole === 6
//             ? authUser?.senior_id
//             : authUser?.id,

//         created_by: authUser?.id,

//         agent_id: localRole === 6
//             ? authUser?.senior_id
//             : authUser?.agent_id,
//         company_id: authUser?.company_id,
//         address1: form.addressLine1,
//         address2: form.addressLine2,
//       };

//       const res = await fetch(
//         `${BASE_URL}warranty/create-customer-new`,
//         {
//           method: "POST",
//           headers: { "Content-Type": "application/json" },
//           body: JSON.stringify(payload),
//         }
//       );

//       const data = await res.json();

//       if (!res.ok) {
//         throw new Error(data?.message || "Server error");
//       }

//       if (data.message === "Customer already exists") {
//         setWCustomerId(data.data.id);
//         toast.success("Customer already exists. Using existing customer.");
//         return true;
//       }

//       if (data.message && data.data?.id) {
//         setWCustomerId(data.data.id);
//         toast.success("Customer created successfully");
//         return true;
//       }

//       throw new Error("Failed to create customer");
//     } catch (err) {
//       toast.error(err.message || "Failed to create customer");
//       return false;
//     }
//   };

//   const uploadImage = async (file, tag) => {
//   try {
//     const fd = new FormData();
//     fd.append("file", file);
//     fd.append("tag", tag);

//     const res = await fetch(`${BASE_URL}warranty/upload-file`, {
//       method: "POST",
//       body: fd,
//     });

//     const data = await res.json();

//     if (!res.ok || !data.status) {
//       throw new Error(data.message || "Upload failed");
//     }

//     return data.url;
//   } catch (err) {
//     toast.error(err.message || "Image upload failed");
//     return null;
//   }
// };

// const handleImageUpload = async (field, file, tag) => {
//   if (!file) return;

//   try {
//     const options = {
//       maxSizeMB: 5,              // target max size
//       maxWidthOrHeight: 1920,   // resize large images
//       useWebWorker: true,
//     };

//     const compressedFile = await imageCompression(file, options);

//     console.log(
//       "Original size:",
//       (file.size / 1024 / 1024).toFixed(2),
//       "MB"
//     );

//     console.log(
//       "Compressed size:",
//       (compressedFile.size / 1024 / 1024).toFixed(2),
//       "MB"
//     );

//     if (compressedFile.size > 5 * 1024 * 1024) {
//       toast.error("Image still larger than 5MB after compression");
//       return;
//     }

//     setUploading((p) => ({ ...p, [field]: true }));

//     const toastId = toast.loading("Uploading image, please wait...");

//     const fd = new FormData();
//     fd.append("file", compressedFile);
//     fd.append("tag", tag);

//     const res = await fetch(`${BASE_URL}warranty/upload-file`, {
//       method: "POST",
//       body: fd,
//     });

//     const data = await res.json();

//     const fileUrl = data?.data?.file_url;
//     if (!fileUrl) throw new Error("Upload failed");

//     setForm((prev) => ({
//       ...prev,
//       [field]: fileUrl,
//     }));

//     setErrors((prev) => ({ ...prev, [field]: "" }));

//     toast.success("Image uploaded successfully", { id: toastId });

//   } catch (err) {
//     toast.error(err.message || "Upload failed");
//   } finally {
//     setUploading((p) => ({ ...p, [field]: false }));
//   }
// };


// const createWarranty = async ({ is_pay_later }) => {
//   if (isSubmitting) return null;

//   try {
//     setIsSubmitting(true);

//      if (!wCustomerId) {
//       toast.error("Customer not found");
//       return null;
//     }

//     if (!selectedTemplate?.warranty_product) {
//       toast.error("Warranty product missing");
//       return null;
//     }
//     const product = selectedTemplate.warranty_product;

//     const payload = {
//       imei1: form.deviceImei,
//       imei2: form.deviceImei2 || null,
//       serial: form.serialNumber,

//       brand_id: Number(form.deviceBrand),
//       brand_name: form.brandName,

//       category_id: Number(form.category),
//       category_name: form.categoryName,

//       product_id: product.id,
//       product_name: product.name,

//       available_claim: product.claims,
//       expiry_date: form.expiryDate,

//       document_url: form.billPhoto,
//       link1: form.devicePhoto,
//       link2: form.customerPhoto,

//       w_customer_id: wCustomerId,
//       device_price: Number(form.mopPrice),
//       retailer_id: authUser?.id,
//       name: form.name,
//       model: form.deviceModel,
//       model_id: form.modelId,
//       product_mrp:product.mrp,
//       product_price: calculatedPayouts.retailerPending,
//       retailer_payout: calculatedPayouts.retailerPayout,
//       employee_payout: calculatedPayouts.employeePayout,
//       other_payout: calculatedPayouts.otherpayout,
//       company_payout: calculatedPayouts.companypayout,
//       agent_id:authUser?.senior_id,
//       company_id:authUser.company_id,
//       created_by: authUser?.id,
//       is_pay_later,
//     };

//     const res = await fetch(`${BASE_URL}warranty/create-warranty`, {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify(payload),
//     });

//     const data = await res.json();
//     if (!res.ok || !data.device?.id) {
//         throw new Error(data?.message || "Warranty creation failed");
//       }

//     return data.device.id;
//   } catch (err) {
//     toast.error(err.message || "Warranty creation failed");
//     return null;
//   }
// };

//   const generateWarrantyCertificate = async () => {
//   try {
//     if (!form.deviceImei) {
//       toast.error("IMEI is missing");
//       return false;
//     }

//     const res = await fetch(
//       `${BASE_URL}warranty/generate-wcertificate`,
//       {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({
//           imei1: form.deviceImei,   // ✅ ONLY PAYLOAD
//         }),
//       }
//     );

//     const data = await res.json();

//     if (!res.ok || data.success === false) {
//       throw new Error(data?.message || "Certificate generation failed");
//     }

//     // ✅ STORE CERTIFICATE INFO
//     setCertificate({
//       certificateId: data.certificate_id,
//       certificateUrl: data.certificate_url,
//     });

//     toast.success("Warranty certificate generated");
//     return true;
//   } catch (err) {
//     toast.error(err.message || "Failed to generate certificate");
//     return false;
//   }
// };

//   useEffect(() => {
//     const fetchRetailer = async () => {
//       try {
//         setRetailerLoading(true);

//         if (!authUser?.id) {
//           console.warn("authUser.id missing, get-users API skipped");
//           return;
//         }

//         const res = await fetch(
//           `${BASE_URL}common/get-users?id=${authUser.id}`
//         );

//         const json = await res.json();

//         if (!res.ok || !json.status || !json.data?.length) {
//           throw new Error("Failed to fetch retailer data");
//         }

//         const user = json.data[0];

//         setRetailer({
//           id: user.id,
//           name: user.business_name || user.name,
//           mobile: user.contact_phone,
//           email: user.contact_email,
//           company_id: user.company_id,
//           agent_id: user.senior_id,
//           role: user.role,

//           pay_now: Number(user.pay_now),
//           pay_later: Number(user.pay_later),
//           admin_paynow_status: Number(user.admin_paynow_status),
//           admin_paylater_status: Number(user.admin_paylater_status),
//         });
//       } catch (err) {
//         console.error("get-users failed:", err);
//         toast.error(err.message || "Unable to load retailer details");
//       } finally {
//         setRetailerLoading(false);
//       }
//     };

//     fetchRetailer();
//   }, [authUser?.id]); // ✅ IMPORTANT

//   useEffect(() => {
//   if (!retailer) return;

//   setPaymentConfig({
//     pay_now: retailer.pay_now,
//     pay_later: retailer.pay_later,
//     admin_paynow_status: retailer.admin_paynow_status,
//     admin_paylater_status: retailer.admin_paylater_status,
//   });
// }, [retailer]);

//   /* ---------------- NAVIGATION ---------------- */
//   const handleNext = async () => {

//   const isValid = validateStep();

//   const validationErrors = validateStep();

//   if (Object.keys(validationErrors).length > 0) {
//     const firstError = Object.values(validationErrors)[0];
//     toast.error(firstError);
//     return;
//   }

//   if (!isValid) {
//     // 🔥 GET FIRST ERROR MESSAGE
//     const firstError = Object.values(errors)[0];

//     if (firstError) {
//       toast.error(firstError);
//     } else {
//       toast.error("Please fill all required fields");
//     }

//     return;
//   }

//   // STEP 2 → CREATE CUSTOMER
//   if (currentStep === 2) {
//     const ok = await createCustomer();
//     if (!ok) return;
//   }

//   // STEP 4 → CREATE WARRANTY
//   if (currentStep === 4) {
//     if (isSubmitting) return;

//     try {
//       setIsSubmitting(true);

//       if (isPayLater === 1) {
//         const warrantyId = await createWarranty({
//           is_pay_later: 1,
//         });

//         if (!warrantyId) return;
//       }

//       const certOk = await generateWarrantyCertificate();
//       if (!certOk) return;

//       setSubmitted(true);
//     } catch (err) {
//       console.error(err);
//     } finally {
//       setIsSubmitting(false);
//     }

//     return;
//   }

//   setCurrentStep((s) => s + 1);
// };


//   const handleBack = () => {
//     if (currentStep > 1) setCurrentStep((s) => s - 1);
//   };

//   useEffect(() => {
//   if (currentStep !== 4) {
//     setConfirmed(false);
//   }
// }, [currentStep]);


// const isRetailerMobile =
//   typeof window !== "undefined" &&
//   window.innerWidth < 640 &&
//   authUser?.role === 5;

//   /* ---------------- UI ---------------- */
//   return (
//     <div
//       className={`
//         rounded-2xl p-3 transition-all duration-300
//         ${
//           isRetailerMobile
//             ? "bg-white/10 backdrop-blur-2xl border border-white/30 shadow-[0_20px_50px_rgba(31,38,135,0.25)]"
//             : "bg-white border shadow"
//         }
//       `}
//     >
//       <Header />
//       <Stepper steps={steps} currentStep={currentStep} />

//       {submitted ? (
//         <SuccessSummary
//             form={form}
//             certificate={certificate}
//           />
//       ) : (
//         <>
//           <FormBody
//             currentStep={currentStep}
//             form={form}
//             errors={errors}
//             updateField={updateField}
//             categories={categories}
//             brands={brands}
//             modelResults={modelResults}
//             modelLoading={modelLoading}
//             onSearchModel={searchDeviceModels}
//             onViewTemplates={handleViewTemplates}
//             priceTemplates={priceTemplates}
//             selectedTemplate={selectedTemplate}
//             setSelectedTemplate={setSelectedTemplate}
//             noTemplateMessage={noTemplateMessage}
//             loading={pricingLoading}
//             onPincodeChange={fetchPincodeDetails}
//             pinLoading={pinLoading}
//             handleImageUpload={handleImageUpload}
//             uploading={uploading}
//             confirmed={confirmed}
//             setConfirmed={setConfirmed}
//             isPhoneVerified={isPhoneVerified}
//             setIsPhoneVerified={setIsPhoneVerified}
//             setCalculatedPayouts={setCalculatedPayouts}
//             paymentConfig={paymentConfig}
//             loadingPaymentConfig={loadingPaymentConfig}
//             retailer={retailer}
//             retailerLoading={retailerLoading}   
//             setIsPayLater={setIsPayLater}
//             wCustomerId={wCustomerId}
//           />

//           <Footer
//             currentStep={currentStep}
//             onBack={handleBack}
//             onNext={handleNext}
//             isLast={currentStep === steps.length}
//             confirmed={confirmed}
//             isSubmitting={isSubmitting}
//           />
//         </>
//       )}
//     </div>
//   );
// }


// import { useEffect, useState } from "react";
// import { Header } from "./Header";
// import { Stepper } from "./Stepper";
// import { SuccessSummary } from "./SuccessSummary";
// import { FormBody } from "./FormBody";
// import { Footer } from "./Footer";
// import { useLocation } from "react-router-dom";
// import toast from "react-hot-toast";
// import { BASE_URL } from "../../../../config/api";

// /* ---------------- VALIDATION HELPERS ---------------- */
// const isValidEmail = (email) =>
//   /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// const isValidImei = (imei) =>
//   /^\d{15}$/.test(imei);

// const isValidSerial = (serial) =>
//   /^[A-Za-z0-9-]{5,}$/.test(serial);

// const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB


// /* ---------------- STEPS ---------------- */
// const steps = [
//   { id: 1, name: "Select Plan" },
//   { id: 2, name: "Contact Details" },
//   { id: 3, name: "Device Details" },
//   { id: 4, name: "Payment" },
// ];

// export default function CreateCustomer() {
//   const [currentStep, setCurrentStep] = useState(1);
//   const [errors, setErrors] = useState({});
//   const [submitted, setSubmitted] = useState(false);
//   const [wCustomerId, setWCustomerId] = useState(null);
//   const location = useLocation();
//   const existingCustomer = location.state?.customer || null;
//   const verifiedMobile = location.state?.verifiedMobile || "";
//   const [confirmed, setConfirmed] = useState(false);
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [noTemplateMessage, setNoTemplateMessage] = useState("");
//   const [isPayLater, setIsPayLater] = useState(0); // 0 = Pay Now, 1 = Pay Later
//   const [uploading, setUploading] = useState({
//   billPhoto: false,
//   devicePhoto: false,
//   customerPhoto: false,
// });





//   const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");
//   const companyId = authUser?.company_id;

//   /* ---------------- FORM STATE ---------------- */
//   const [form, setForm] = useState({
//     name: "",
//     mobile: "",
//     email: "",
//     state: "",
//     city: "",
//     pincode: "",
//     location: "",
//     addressLine1: "",
//     addressLine2: "",
//     category: "",
//     categoryName: "",
//     deviceBrand: "",
//     brandName: "",
//     deviceModel: "",
//     mopPrice: "",
//     expiryDate: "",
//     paymentMode: "",
//     deviceImei: "",
//     deviceImei2: "",
//     serialNumber: "",
//     billPhoto: "",       // URL
//     devicePhoto: "",     // URL
//     customerPhoto: "", 
//     modelId: null,
//   });

//   /* ---------------- STATE ---------------- */
//   const [categories, setCategories] = useState([]);
//   const [brands, setBrands] = useState([]);
//   const [priceTemplates, setPriceTemplates] = useState([]);
//   const [selectedTemplate, setSelectedTemplate] = useState(null);

//   const [modelResults, setModelResults] = useState([]);
//   const [modelLoading, setModelLoading] = useState(false);
//   const [pricingLoading, setPricingLoading] = useState(false);
//   const [pinLoading, setPinLoading] = useState(false);

//   const [certificate, setCertificate] = useState(null);
//   const [isPhoneVerified, setIsPhoneVerified] = useState(false);

//   const [paymentConfig, setPaymentConfig] = useState(null);
//   console.log("payment status: ", paymentConfig);
//   const [loadingPaymentConfig, setLoadingPaymentConfig] = useState(false);
//   const [retailer, setRetailer] = useState(null);
//   const [retailerLoading, setRetailerLoading] = useState(false);



//   const [calculatedPayouts, setCalculatedPayouts] = useState({
//     retailerPayout: 0,
//     employeePayout: 0,
//     retailerPending: 0,
//     otherpayout:0,
//     companypayout: 0,
//     productPrice: 0,
//   });


//   /* ---------------- HELPERS ---------------- */
//   const updateField = (field, value) => {
//     setForm((p) => ({ ...p, [field]: value }));
//     setErrors((e) => ({ ...e, [field]: "" }));
//   };

//   const handleFileChange = (field, file) => {
//   if (!file) return;

//   if (!file.type.startsWith("image/")) {
//     toast.error("Only image files allowed");
//     return;
//   }

//   setForm((prev) => ({
//     ...prev,
//     [field]: file,
//   }));

//   setErrors((prev) => ({ ...prev, [field]: "" }));
// };


//   /* ---------------- DEVICE MODEL SEARCH ---------------- */
//   const searchDeviceModels = async (query) => {
//     if (!query || query.length < 2) return setModelResults([]);
//     try {
//       setModelLoading(true);
//       const res = await fetch(
//         `${BASE_URL}warranty/device-models?search=${query}`
//       );
//       const data = await res.json();
//       setModelResults(data?.data?.data || []);
//     } catch {
//       toast.error("Failed to load models");
//     } finally {
//       setModelLoading(false);
//     }
//   };

//   /* ---------------- PINCODE ---------------- */
//   const fetchPincodeDetails = async (pin) => {
//     if (pin.length !== 6) return;
//     try {
//       setPinLoading(true);
//       const res = await fetch(`${BASE_URL}pincode/${pin}`);
//       const data = await res.json();
//       if (data.success) {
//         updateField("state", data.state);
//         updateField("city", data.district);
//       } else {
//         toast.error("Invalid pincode");
//       }
//     } finally {
//       setPinLoading(false);
//     }
//   };

//   /* ---------------- FETCH DATA ---------------- */
//   useEffect(() => {
//     fetch(`${BASE_URL}warranty/get-categories`)
//       .then((r) => r.json())
//       .then(setCategories);
//     fetch(`${BASE_URL}warranty/get-brands`)
//       .then((r) => r.json())
//       .then((d) => setBrands(d.data || []));
//   }, []);

//   /* ---------------- PRICE TEMPLATES ---------------- */
//   const handleViewTemplates = async () => {
//     if (!form.category || !form.mopPrice) {
//       toast.error("Missing data");
//       return;
//     }

//     try {
//       setPricingLoading(true);
//       setNoTemplateMessage("");      // reset
//       setPriceTemplates([]);         // clear old data

//       const res = await fetch(`${BASE_URL}warranty/matching-price-templates`, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({
//           product_price: Number(form.mopPrice),
//           category_id: Number(form.category),
//           company_id: Number(companyId),
//         }),
//       });

//       const data = await res.json();

//       // ❌ NO DATA FOUND
//       if (data.status === false || !data.data?.length) {
//         setNoTemplateMessage(
//           data.message || "No matching price templates found"
//         );
//         return;
//       }

//       // ✅ DATA FOUND
//       setPriceTemplates(data.data);
//     } catch (err) {
//       toast.error("Failed to fetch templates");
//     } finally {
//       setPricingLoading(false);
//     }
//   };


//   /* ---------------- VALIDATION ---------------- */
//   const validateStep = () => {
//     const e = {};

//     if (currentStep === 1) {
//       if (!selectedTemplate) e.template = "Select a template";
//     }

//     if (currentStep === 2) {
//       if (!form.name) e.name = "Name is required";

//       if (!form.email) {
//         e.email = "Email is required";
//       } else if (!isValidEmail(form.email)) {
//         e.email = "Enter a valid email";
//       }

//       if (!form.pincode) e.pincode = "Pincode is required";
//       if (!form.location) e.location = "Location is required";
//       if (!form.addressLine1) e.addressLine1 = "Address is required";
//     }

//     if (currentStep === 3) {
//       // IMEI 1
//       if (!form.deviceImei) {
//         e.deviceImei = "IMEI is required";
//       } else if (!isValidImei(form.deviceImei)) {
//         e.deviceImei = "IMEI must be 15 digits";
//       }

//       // IMEI 2 (optional)
//       if (form.deviceImei2 && !isValidImei(form.deviceImei2)) {
//         e.deviceImei2 = "IMEI 2 must be 15 digits";
//       }

//       // Serial
//       if (!form.serialNumber) {
//         e.serialNumber = "Serial number is required";
//       } else if (!isValidSerial(form.serialNumber)) {
//         e.serialNumber = "Invalid serial number format";
//       }

//       // Images
//       if (!form.billPhoto) e.billPhoto = "Bill photo required";
//       // if (!form.devicePhoto) e.devicePhoto = "Device photo required";
//       // if (!form.customerPhoto) e.customerPhoto = "Customer photo required";
//     }

//     // if (currentStep === 4 && !form.paymentMode) {
//     //   e.paymentMode = "Select payment option";
//     // }

//     setErrors(e);
//     return Object.keys(e).length === 0;
//   };

//   /* ---------------- CREATE CUSTOMER ---------------- */
//   const createCustomer = async () => {
//     try {
//       const res = await fetch(
//         `${BASE_URL}warranty/create-customer-new`,
//         {
//           method: "POST",
//           headers: { "Content-Type": "application/json" },
//           body: JSON.stringify({
//             name: form.name,
//             mobile: form.mobile,
//             email: form.email,
//             state: form.state,
//             city: form.city,
//             pincode: form.pincode,
//             location: form.location,
//             retailer_id: authUser?.id,
//             created_by: authUser?.id,
//             agent_id:authUser.senior_id,
//             company_id:authUser.company_id,
//             address1:form.addressLine1,
//             address2:form.addressLine2,
//           }),
//         }
//       );

//       const data = await res.json();

//       if (!res.ok) {
//         throw new Error(data?.message || "Server error");
//       }

//       // ✅ EXISTING CUSTOMER
//       if (data.message === "Customer already exists") {
//         setWCustomerId(data.data.id);
//         toast.success("Customer already exists. Using existing customer.");
//         return true;
//       }

//       // ✅ NEW CUSTOMER CREATED
//       if (data.message && data.data?.id) {
//         setWCustomerId(data.data.id);
//         toast.success("Customer created successfully");
//         return true;
//       }

//       throw new Error("Failed to create customer");
//     } catch (err) {
//       toast.error(err.message || "Failed to create customer");
//       return false;
//     }
//   };


//   const uploadImage = async (file, tag) => {
//   try {
//     const fd = new FormData();
//     fd.append("file", file);
//     fd.append("tag", tag);

//     const res = await fetch(`${BASE_URL}warranty/upload-file`, {
//       method: "POST",
//       body: fd,
//     });

//     const data = await res.json();

//     if (!res.ok || !data.status) {
//       throw new Error(data.message || "Upload failed");
//     }

//     return data.url;
//   } catch (err) {
//     toast.error(err.message || "Image upload failed");
//     return null;
//   }
// };

// // const handleImageUpload = async (field, file, tag) => {
// //   if (!file) return;

// //   if (!file.type.startsWith("image/")) {
// //     toast.error("Only image files allowed");
// //     return;
// //   }

// //   try {
// //     const fd = new FormData();
// //     fd.append("file", file);
// //     fd.append("tag", tag);

// //     const res = await fetch(`${BASE_URL}warranty/upload-file`, {
// //       method: "POST",
// //       body: fd,
// //     });

// //     const data = await res.json();

// //     if (!res.ok) {
// //       throw new Error(data?.message || "Upload failed");
// //     }

// //     // ✅ THIS IS WHAT WE NEED
// //     const fileUrl = data?.data?.file_url;

// //     if (!fileUrl) {
// //       throw new Error("File URL not returned");
// //     }

// //     // ✅ SAVE URL IN FORM STATE
// //     setForm((prev) => ({
// //       ...prev,
// //       [field]: fileUrl,
// //     }));

// //     setErrors((prev) => ({ ...prev, [field]: "" }));

// //     toast.success("Image uploaded successfully");
// //   } catch (err) {
// //     toast.error(err.message || "Image upload failed");
// //   }
// // };


// const handleImageUpload = async (field, file, tag) => {
//   if (!file) return;

//   setUploading((p) => ({ ...p, [field]: true }));

//   try {
//     const fd = new FormData();
//     fd.append("file", file);
//     fd.append("tag", tag);

//     const res = await fetch(`${BASE_URL}warranty/upload-file`, {
//       method: "POST",
//       body: fd,
//     });

//     const data = await res.json();

//     const fileUrl = data?.data?.file_url;
//     if (!fileUrl) throw new Error("Upload failed");

//     setForm((prev) => ({
//       ...prev,
//       [field]: fileUrl,
//     }));

//     setErrors((prev) => ({ ...prev, [field]: "" }));
//     toast.success("Image uploaded");
//   } catch (err) {
//     toast.error(err.message || "Upload failed");
//   } finally {
//     setUploading((p) => ({ ...p, [field]: false }));
//   }
// };


// // const createWarranty = async () => {
// //   if (isSubmitting) return null; // 🔒 HARD STOP

// //   try {
// //     setIsSubmitting(true);

//     // if (!wCustomerId) {
//     //   toast.error("Customer not found");
//     //   return null;
//     // }

//     // if (!selectedTemplate?.warranty_product) {
//     //   toast.error("Warranty product missing");
//     //   return null;
//     // }

// //     const product = selectedTemplate.warranty_product;

//     // const payload = {
//     //   imei1: form.deviceImei,
//     //   imei2: form.deviceImei2 || null,
//     //   serial: form.serialNumber,

//     //   brand_id: Number(form.deviceBrand),
//     //   brand_name: form.brandName,

//     //   category_id: Number(form.category),
//     //   category_name: form.categoryName,

//     //   product_id: product.id,
//     //   product_name: product.name,

//     //   available_claim: product.claims,
//     //   expiry_date: form.expiryDate,

//     //   document_url: form.billPhoto,
//     //   link1: form.devicePhoto,
//     //   link2: form.customerPhoto,

//     //   w_customer_id: wCustomerId,
//     //   device_price: Number(form.mopPrice),
//     //   retailer_id: authUser?.id,
//     //   name: form.name,
//     //   model: form.deviceModel,
//     //   product_price: calculatedPayouts.retailerPending,
//     //   retailer_payout: calculatedPayouts.retailerPayout,
//     //   employee_payout: calculatedPayouts.employeePayout,
//     //   other_payout: calculatedPayouts.otherpayout,
//     //   company_payout: calculatedPayouts.companypayout,
//     //   agent_id:authUser?.senior_id,
//     //   company_id:authUser.company_id,
//     //   created_by: authUser?.id,
//     // };

// //     const res = await fetch(`${BASE_URL}warranty/create-warranty`, {
// //       method: "POST",
// //       headers: { "Content-Type": "application/json" },
// //       body: JSON.stringify(payload),
// //     });

// //     const data = await res.json();

// //     if (!res.ok || data.status === false) {
// //       throw new Error(data?.message || "Failed to create warranty");
// //     }

// //     const warrantyId = data?.device?.id;

// //     if (!warrantyId) {
// //       throw new Error("Warranty ID not returned");
// //     }

// //     toast.success("Warranty created successfully");
// //     return warrantyId;
// //   } catch (err) {
// //     toast.error(err.message || "Warranty creation failed");
// //     return null;
// //   } finally {
// //     setIsSubmitting(false); // ✅ ALWAYS RELEASE
// //   }
// // };

// const createWarranty = async ({ is_pay_later }) => {
//   if (isSubmitting) return null;

//   try {
//     setIsSubmitting(true);

//      if (!wCustomerId) {
//       toast.error("Customer not found");
//       return null;
//     }

//     if (!selectedTemplate?.warranty_product) {
//       toast.error("Warranty product missing");
//       return null;
//     }
//     const product = selectedTemplate.warranty_product;

//     const payload = {
//       imei1: form.deviceImei,
//       imei2: form.deviceImei2 || null,
//       serial: form.serialNumber,

//       brand_id: Number(form.deviceBrand),
//       brand_name: form.brandName,

//       category_id: Number(form.category),
//       category_name: form.categoryName,

//       product_id: product.id,
//       product_name: product.name,

//       available_claim: product.claims,
//       expiry_date: form.expiryDate,

//       document_url: form.billPhoto,
//       link1: form.devicePhoto,
//       link2: form.customerPhoto,

//       w_customer_id: wCustomerId,
//       device_price: Number(form.mopPrice),
//       retailer_id: authUser?.id,
//       name: form.name,
//       model: form.deviceModel,
//       model_id: form.modelId,
//       product_mrp:product.mrp,
//       product_price: calculatedPayouts.retailerPending,
//       retailer_payout: calculatedPayouts.retailerPayout,
//       employee_payout: calculatedPayouts.employeePayout,
//       other_payout: calculatedPayouts.otherpayout,
//       company_payout: calculatedPayouts.companypayout,
//       agent_id:authUser?.senior_id,
//       company_id:authUser.company_id,
//       created_by: authUser?.id,
//       is_pay_later,
//     };

//     const res = await fetch(`${BASE_URL}warranty/create-warranty`, {
//       method: "POST",
//       headers: { "Content-Type": "application/json" },
//       body: JSON.stringify(payload),
//     });

//     const data = await res.json();
//     if (!res.ok || !data.device?.id) {
//         throw new Error(data?.message || "Warranty creation failed");
//       }

//     return data.device.id;
//   } catch (err) {
//     toast.error(err.message || "Warranty creation failed");
//     return null;
//   }
// };

//   const generateWarrantyCertificate = async () => {
//   try {
//     if (!form.deviceImei) {
//       toast.error("IMEI is missing");
//       return false;
//     }

//     const res = await fetch(
//       `${BASE_URL}warranty/generate-wcertificate`,
//       {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({
//           imei1: form.deviceImei,   // ✅ ONLY PAYLOAD
//         }),
//       }
//     );

//     const data = await res.json();

//     if (!res.ok || data.success === false) {
//       throw new Error(data?.message || "Certificate generation failed");
//     }

//     // ✅ STORE CERTIFICATE INFO
//     setCertificate({
//       certificateId: data.certificate_id,
//       certificateUrl: data.certificate_url,
//     });

//     toast.success("Warranty certificate generated");
//     return true;
//   } catch (err) {
//     toast.error(err.message || "Failed to generate certificate");
//     return false;
//   }
// };


//   useEffect(() => {
//     const fetchRetailer = async () => {
//       try {
//         setRetailerLoading(true);

//         if (!authUser?.id) {
//           console.warn("authUser.id missing, get-users API skipped");
//           return;
//         }

//         const res = await fetch(
//           `${BASE_URL}common/get-users?id=${authUser.id}`
//         );

//         const json = await res.json();

//         if (!res.ok || !json.status || !json.data?.length) {
//           throw new Error("Failed to fetch retailer data");
//         }

//         const user = json.data[0];

//         setRetailer({
//           id: user.id,
//           name: user.business_name || user.name,
//           mobile: user.contact_phone,
//           email: user.contact_email,
//           company_id: user.company_id,
//           agent_id: user.senior_id,
//           role: user.role,

//           pay_now: Number(user.pay_now),
//           pay_later: Number(user.pay_later),
//           admin_paynow_status: Number(user.admin_paynow_status),
//           admin_paylater_status: Number(user.admin_paylater_status),
//         });
//       } catch (err) {
//         console.error("get-users failed:", err);
//         toast.error(err.message || "Unable to load retailer details");
//       } finally {
//         setRetailerLoading(false);
//       }
//     };

//     fetchRetailer();
//   }, [authUser?.id]); // ✅ IMPORTANT

//   useEffect(() => {
//   if (!retailer) return;

//   setPaymentConfig({
//     pay_now: retailer.pay_now,
//     pay_later: retailer.pay_later,
//     admin_paynow_status: retailer.admin_paynow_status,
//     admin_paylater_status: retailer.admin_paylater_status,
//   });
// }, [retailer]);



// // useEffect(() => {
// //   const fetchRetailer = async () => {
// //     try {
// //       setRetailerLoading(true);

// //       const authUser = JSON.parse(
// //         localStorage.getItem("authUserData") || "{}"
// //       );

// //       if (!authUser?.id) return;

// //       const res = await fetch(
// //         `${BASE_URL}common/get-users?id=${authUser.id}`
// //       );

// //       const json = await res.json();

// //       if (!res.ok || !json.status || !json.data?.length) {
// //         throw new Error("Failed to fetch retailer data");
// //       }

// //       const user = json.data[0];

// //       // ✅ NORMALIZED RETAILER OBJECT
// //       setRetailer({
// //         id: user.id,
// //         name: user.business_name || user.name,
// //         mobile: user.contact_phone,
// //         email: user.contact_email,
// //         company_id: user.company_id,
// //         agent_id: user.senior_id,
// //         role: user.role,

// //         // payment config (reuse later)
// //         pay_now: Number(user.pay_now),
// //         pay_later: Number(user.pay_later),
// //         admin_paynow_status: Number(user.admin_paynow_status),
// //         admin_paylater_status: Number(user.admin_paylater_status),
// //       });
// //     } catch (err) {
// //       toast.error(err.message || "Unable to load retailer details");
// //     } finally {
// //       setRetailerLoading(false);
// //     }
// //   };

// //   fetchRetailer();
// // }, []);


//   /* ---------------- NAVIGATION ---------------- */
//   const handleNext = async () => {
//   if (!validateStep()) return;

//   // STEP 2 → CREATE CUSTOMER
//   if (currentStep === 2) {
//     const ok = await createCustomer();
//     if (!ok) return;
//   }

//   // ✅ STEP 3 → FETCH PAYMENT CONFIG
// //   if (currentStep === 3) {
// //   if (!retailer) {
// //     toast.error("Retailer data not loaded");
// //     return;
// //   }

// //   setPaymentConfig({
// //     pay_now: retailer.pay_now,
// //     pay_later: retailer.pay_later,
// //     admin_paynow_status: retailer.admin_paynow_status,
// //     admin_paylater_status: retailer.admin_paylater_status,
// //   });
// // }


//   // STEP 4 → CREATE WARRANTY
//   if (currentStep === 4) {
//   if (isSubmitting) return;

//   try {
//     setIsSubmitting(true);

//     // 🔹 PAY LATER FLOW
//     if (isPayLater === 1) {
//       const warrantyId = await createWarranty({
//         is_pay_later: 1, // ✅ PASS TO API
//       });

//       if (!warrantyId) return;
//     }

//     // 🔹 GENERATE CERTIFICATE (ALWAYS)
//     const certOk = await generateWarrantyCertificate();
//     if (!certOk) return;

//     setSubmitted(true);
//   } catch (err) {
//     console.error(err);
//   } finally {
//     setIsSubmitting(false); // 🔓 RELEASE BUTTON
//   }

//   return;
// }

  
//   setCurrentStep((s) => s + 1);
// };




//   const handleBack = () => {
//     if (currentStep > 1) setCurrentStep((s) => s - 1);
//   };

//   useEffect(() => {
//   if (currentStep !== 4) {
//     setConfirmed(false);
//   }
// }, [currentStep]);


//   /* ---------------- UI ---------------- */
//   return (
//     <div className="bg-white rounded-2xl border shadow p-3">
//       <Header />
//       <Stepper steps={steps} currentStep={currentStep} />

//       {submitted ? (
//         <SuccessSummary
//             form={form}
//             certificate={certificate}
//           />
//       ) : (
//         <>
//           <FormBody
//             currentStep={currentStep}
//             form={form}
//             errors={errors}
//             updateField={updateField}
//             categories={categories}
//             brands={brands}
//             modelResults={modelResults}
//             modelLoading={modelLoading}
//             onSearchModel={searchDeviceModels}
//             onViewTemplates={handleViewTemplates}
//             priceTemplates={priceTemplates}
//             selectedTemplate={selectedTemplate}
//             setSelectedTemplate={setSelectedTemplate}
//             noTemplateMessage={noTemplateMessage}
//             loading={pricingLoading}
//             onPincodeChange={fetchPincodeDetails}
//             pinLoading={pinLoading}
//             handleImageUpload={handleImageUpload}
//             confirmed={confirmed}
//             setConfirmed={setConfirmed}
//             isPhoneVerified={isPhoneVerified}
//             setIsPhoneVerified={setIsPhoneVerified}
//             setCalculatedPayouts={setCalculatedPayouts}
//             paymentConfig={paymentConfig}
//             loadingPaymentConfig={loadingPaymentConfig}
//             retailer={retailer}
//             retailerLoading={retailerLoading}   
//             setIsPayLater={setIsPayLater}
//             wCustomerId={wCustomerId}
//           />

//           <Footer
//             currentStep={currentStep}
//             onBack={handleBack}
//             onNext={handleNext}
//             isLast={currentStep === steps.length}
//             confirmed={confirmed}
//             isSubmitting={isSubmitting}
//           />
//         </>
//       )}
//     </div>
//   );
// }


// import { useState } from "react";
// import { Header } from "./Header";
// import { Stepper } from "./Stepper";
// import { SuccessSummary } from "./SuccessSummary";
// import { FormBody } from "./FormBody";
// import { Footer } from "./Footer";
// import { useLocation } from "react-router-dom";
// import toast from "react-hot-toast";
// import { useEffect } from "react";
// import { BASE_URL } from "../../../../config/api";


// /* ---------------- VALIDATION HELPERS ---------------- */
// const isValidEmail = (email) =>
//   /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// const isValidMobile = (mobile) =>
//   /^[6-9]\d{9}$/.test(mobile);

// const isValidPincode = (pincode) =>
//   /^\d{6}$/.test(pincode);

// const isValidImei = (imei) =>
//   /^[0-9A-Za-z-]{8,15}$/.test(imei);

// /* ---------------- STEPS ---------------- */
// const steps = [
//   { id: 1, name: "Select Plan" },
//   { id: 2, name: "Contact Details" },
//   { id: 3, name: "Location Details" },
//   { id: 4, name: "Device Details" },
//   { id: 5, name: "Payment" },
// ];

// /* ===============================
//   CREATE CUSTOMER
// ================================ */
// export default function CreateCustomer() {
//   const [currentStep, setCurrentStep] = useState(1);
//   const [errors, setErrors] = useState({});
//   const [submitted, setSubmitted] = useState(false);

//   const location = useLocation();

//   const existingCustomer = location.state?.customer || null;
//   const verifiedMobile = location.state?.verifiedMobile || "";
//   console.log("verifiedMobile", verifiedMobile)


//   const [categories, setCategories] = useState([]);
//   const [categoriesLoading, setCategoriesLoading] = useState(false);
//   const [brands, setBrands] = useState([]);
//   const [brandsLoading, setBrandsLoading] = useState(false);
//   const [priceTemplates, setPriceTemplates] = useState([]);
//   const [selectedTemplate, setSelectedTemplate] = useState(null);
//   const [pricingLoading, setPricingLoading] = useState(false);
//   const [pinLoading, setPinLoading] = useState(false);

//   const [modelResults, setModelResults] = useState([]);
//   const [modelLoading, setModelLoading] = useState(false);

//   const authUserString = localStorage.getItem("authUserData");
//   const authUser = authUserString ? JSON.parse(authUserString) : null;
//   const companyId = authUser?.company_id;

//   /* ---------------- FORM STATE ---------------- */
//   const [form, setForm] = useState({
//     name: existingCustomer?.name || "",
//     mobile: existingCustomer?.mobile || "",
//     email: existingCustomer?.email || "",
//     state: existingCustomer?.state || "",
//     city: existingCustomer?.city || "",
//     pincode: existingCustomer?.pincode || "",
//     location: existingCustomer?.location || "",
//     expiryDate: "",
//     deviceBrand: "",
//     deviceModel: "",
//     deviceImei: "",
//     devicePrice: "",
//     paymentMode: "",
//     category: "",        // ID (number/string)
//     categoryName: "",    // DISPLAY    // ID
//     brandName: "", 
//     mopPrice: "",    // DISPLAY
//     addressLine1: "",
//     addressLine2: "",
//   });

//   const searchDeviceModels = async (query) => {
//   if (!query || query.length < 2) {
//     setModelResults([]);
//     return;
//   }

//   try {
//     setModelLoading(true);

//     const res = await fetch(
//       `${BASE_URL}warranty/device-models?search=${query}`
//     );
//     const data = await res.json();

//     if (!res.ok) throw new Error();

//     setModelResults(data?.data?.data || []);
//   } catch {
//     toast.error("Failed to fetch device models");
//   } finally {
//     setModelLoading(false);
//   }
// };


//   useEffect(() => {
//     if (verifiedMobile) {
//       setForm((prev) => ({
//         ...prev,
//         mobile: verifiedMobile,
//       }));
//     }
//   }, [verifiedMobile]); 

//   const fetchPincodeDetails = async (pincode) => {
//   if (!isValidPincode(pincode)) return;

//   try {
//     setPinLoading(true);

//     const res = await fetch(`${BASE_URL}pincode/${pincode}`);
//     const data = await res.json();

//     if (data.success) {
//       setForm((prev) => ({
//         ...prev,
//         state: data.state || "",
//         city: data.district || "",
//         state_code: data.state_code || "",
//         district_code: data.district_code || "",
//       }));

//       setErrors((prev) => ({ ...prev, pincode: "" }));
//     } else {
//       setForm((prev) => ({
//         ...prev,
//         state: "",
//         city: "",
//         state_code: "",
//         district_code: "",
//       }));

//       setErrors((prev) => ({ ...prev, pincode: "Invalid pincode" }));
//       toast.error("Invalid pincode");
//     }
//   } catch {
//     toast.error("Unable to fetch pincode details");
//   } finally {
//     setPinLoading(false);
//   }
// };


//   /* ---------------- PACKAGE STATE ---------------- */
//   const [packages] = useState([
//     {
//       id: 1,
//       package_name: "Basic Warranty",
//       amount: 499,
//       validity_days: 365,
//     },
//     {
//       id: 2,
//       package_name: "Premium Warranty",
//       amount: 999,
//       validity_days: 730,
//     },
//   ]);

//   const [selectedPackage, setSelectedPackage] = useState(null);
//   const [packageLoading] = useState(false);

//   /* ---------------- UPDATE FIELD ---------------- */
//   const updateField = (field, value) => {
//     setForm((prev) => ({ ...prev, [field]: value }));
//     setErrors((prev) => ({ ...prev, [field]: "" }));
//   };



//   useEffect(() => {
//     const fetchCategories = async () => {
//       try {
//         const res = await fetch(
//           `${BASE_URL}warranty/get-categories`
//         );
//         const data = await res.json();
//         if (!res.ok) throw new Error();
//         setCategories(data || []);
//       } catch (err) {
//         toast.error("Failed to load categories");
//       } finally {
//         setCategoriesLoading(false);
//       }
//     };

//     fetchCategories();
//   }, []);

//   useEffect(() => {
//   const fetchBrands = async () => {
//     try {
//       setBrandsLoading(true);

//       const res = await fetch(
//         `${BASE_URL}warranty/get-brands`
//       );
//       const data = await res.json();

//       if (!res.ok) throw new Error();

//       setBrands(data.data || []);
//     } catch (err) {
//       toast.error("Failed to load brands");
//     } finally {
//       setBrandsLoading(false);
//     }
//   };

//   fetchBrands();
// }, []);

//   const handleViewTemplates = async () => {
//   if (!form.category) {
//     toast.error("Please select category");
//     return;
//   }

//   if (!form.mopPrice || Number(form.mopPrice) <= 0) {
//     toast.error("Enter valid device price");
//     return;
//   }

//   try {
//     setPricingLoading(true);
//     setPriceTemplates([]);
//     setSelectedTemplate(null);

//     const res = await fetch(
//       `${BASE_URL}warranty/matching-price-templates`,
//       {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({
//           product_price: Number(form.mopPrice),
//           category_id: Number(form.category),
//           company_id: Number(companyId),
//         }),
//       }
//     );

//     const data = await res.json();

//     if (!data.status || data.data.length === 0) {
//       toast.error(data.message || "No matching price templates found");
//       return;
//     }

//     setPriceTemplates(data.data);
//   } catch {
//     toast.error("Failed to fetch price templates");
//   } finally {
//     setPricingLoading(false);
//   }
// };



//   /* ---------------- VALIDATION ---------------- */
//   const validateStep = () => {
//     const newErrors = {};

//    /* STEP 1: PRICING */
//   if (currentStep === 1) {
//     if (!form.category) {
//       newErrors.category = "Category is required";
//     }

//     if (!form.mopPrice) {
//       newErrors.mopPrice = "MOP price is required";
//     } else if (Number(form.mopPrice) <= 0) {
//       newErrors.mopPrice = "MOP price must be greater than 0";
//     }

//     if (!selectedTemplate) {
//       newErrors.priceTemplate = "Please select a price template";
//     }
//     if (!form.deviceBrand) newErrors.deviceBrand = "Device brand is required";
//     if (!form.deviceModel) newErrors.deviceModel = "Device model is required";
//     // if (!form.deviceImei) newErrors.deviceImei = "IMEI is required";
//   }

//     /* STEP 3: DEVICE */
//     if (currentStep === 2) {
//     if (!form.name) newErrors.name = "Name is required";
//     if (!form.mobile) newErrors.mobile = "Mobile is required";
//     if (!form.email) newErrors.email = "Email is required";
//     if (!form.pincode) newErrors.pincode = "Pincode is required";
//     if (!form.state) newErrors.state = "State is required";
//     if (!form.city) newErrors.city = "City is required";
//     if (!form.location) newErrors.location = "Location is required";
//     if (!form.addressLine1) {
//       newErrors.addressLine1 = "Address Line 1 is required";
//     }

// }

//     // if (currentStep === 4) {
//     //   if (!form.deviceBrand)
//     //     newErrors.deviceBrand = "Device brand is required";

//     //   if (!form.deviceModel)
//     //     newErrors.deviceModel = "Device model is required";

//     //   if (!form.deviceImei) {
//     //     newErrors.deviceImei = "IMEI / Serial No is required";
//     //   } else if (!isValidImei(form.deviceImei)) {
//     //     newErrors.deviceImei = "Invalid IMEI / Serial No";
//     //   }

//     //   if (!form.devicePrice) {
//     //     newErrors.devicePrice = "Purchase price is required";
//     //   } else if (Number(form.devicePrice) <= 0) {
//     //     newErrors.devicePrice = "Price must be greater than 0";
//     //   }
//     // }

//     /* STEP 5: PAYMENT */
//     if (currentStep === 5) {
//       if (!form.paymentMode) {
//         newErrors.paymentMode = "Please select Pay Now or Pay Later";
//       }
//     }


//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   useEffect(() => {
//   if (currentStep === 4 && form.mopPrice) {
//     setForm((prev) => ({
//       ...prev,
//       devicePrice: prev.mopPrice,
//     }));
//   }
// }, [currentStep]);


// const createCustomer = async () => {
//   try {
//     const payload = {
//       name: existingCustomer?.name || form.name,
//       mobile: existingCustomer?.mobile || form.mobile,
//       email: existingCustomer?.email || form.email,
//       state: existingCustomer?.state || form.state,
//       city: existingCustomer?.city || form.city,
//       pincode: existingCustomer?.pincode || form.pincode,
//       location: existingCustomer?.location || form.location,
//       retailer_id: authUser?.id, // session user id
//     };

//     const res = await fetch(
//       `${BASE_URL}warranty/create-customer-new`,
//       {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify(payload),
//       }
//     );

//     const data = await res.json();

//     if (!res.ok || data.status === false) {
//       throw new Error(data.message || "Failed to create customer");
//     }

//     toast.success("Customer details saved successfully");
//     return true;
//   } catch (err) {
//     toast.error(err.message);
//     return false;
//   }
// };

//   /* ---------------- NEXT ---------------- */
//   const handleNext = async () => {
//   if (!validateStep()) return;

//   // 🚀 STEP 3 → CREATE CUSTOMER
//   if (currentStep === 3) {
//     const success = await createCustomer();
//     if (!success) return; // ❌ stop navigation if API fails
//   }

//   if (currentStep < steps.length) {
//     setCurrentStep((s) => s + 1);
//     return;
//   }

//   // 🔥 FINAL STEP
//   setSubmitted(true);

//   const payload = {
//     ...form,
//     selectedPackage,
//   };

//   console.log("FINAL PAYLOAD", payload);

//   if (form.paymentMode === "pay_now") {
//     console.log("Proceeding to Pay Now");
//   } else {
//     console.log("Payment marked as Pay Later");
//   }
// };

//   /* ---------------- BACK ---------------- */
//   const handleBack = () => {
//     if (currentStep > 1) setCurrentStep((s) => s - 1);
//   };

//   useEffect(() => {
//   setForm((prev) => ({
//     ...prev,
//     deviceBrand: "",
//   }));
// }, [form.category]);

//   /* ---------------- UI ---------------- */
//   return (
//     <div className="bg-white rounded-2xl shadow-lg border p-6">
//       <Header />
//       <Stepper steps={steps} currentStep={currentStep} />

//       {submitted ? (
//         <SuccessSummary />
//       ) : (
//         <>
//           <FormBody
//             currentStep={currentStep}
//             form={form}
//             errors={errors}
//             updateField={updateField}
//             existingCustomer={existingCustomer}
//             onSearchModel={searchDeviceModels}
//             modelResults={modelResults}
//             modelLoading={modelLoading}

//             verifiedMobile={verifiedMobile} 
//             categories={categories}
//             categoriesLoading={categoriesLoading}
//             brands={brands}
//             priceTemplates={priceTemplates}
//             selectedTemplate={selectedTemplate}
//             setSelectedTemplate={setSelectedTemplate}
//             loading={pricingLoading}
//             onViewTemplates={handleViewTemplates}
//             onPincodeChange={fetchPincodeDetails}   // ✅ NEW
//             pinLoading={pinLoading}   
//           />


//           <Footer
//             currentStep={currentStep}
//             onBack={handleBack}
//             onNext={handleNext}
//             isLast={currentStep === steps.length}
//           />
//         </>
//       )}
//     </div>
//   );
// }
